// SCRUM-1971 — passkey-adoption UX. Shared home for the "should we offer a
// passkey right now?" decision plus the snooze cooldown, so the post-password
// nudge (SCRUM-1972) and the cross-device local offer (SCRUM-1973) reuse one
// implementation. Progressive enhancement throughout: every probe fails safe to
// "don't prompt", so the password and existing passkey flows are never affected.
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import { AuthService } from './auth.service';

/** Which adoption prompt is being offered. */
export type PasskeyPromptKind = 'enrol-nudge' | 'local-offer';

/** The prompt the global banner renders. `null` means nothing is showing. */
export interface PasskeyPrompt {
  kind: PasskeyPromptKind;
  title: string;
  body: string;
  cta: string;
}

@Injectable({ providedIn: 'root' })
export class PasskeyPromptService {
  // "Not now" persists a ~30-day cooldown so we don't nag on every login.
  private readonly SNOOZE_KEY = 'hub_passkey_prompt_snoozed_until';
  private readonly COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

  // The global app-shell banner subscribes to this; login flows push into it.
  private readonly promptSubject = new BehaviorSubject<PasskeyPrompt | null>(null);
  readonly prompt$ = this.promptSubject.asObservable();

  // Guards the enrolment ceremony against a double-tap.
  private busy = false;

  constructor(private readonly auth: AuthService) {}

  /** Whether the user recently chose "Not now" — suppresses every passkey
   *  nudge/offer until the cooldown lapses. Fails safe to false. */
  isSnoozed(): boolean {
    try {
      const until = localStorage.getItem(this.SNOOZE_KEY);
      if (!until) return false;
      const ts = Number.parseInt(until, 10);
      if (!Number.isFinite(ts)) return false;
      return Date.now() < ts;
    } catch {
      return false;
    }
  }

  /**
   * Story 1 (SCRUM-1972). After a PASSWORD login, nudge the user to set up a
   * passkey when: the device can actually make one, they have none yet, and they
   * haven't snoozed. Never blocks landing on the app — the banner appears after
   * navigation. Any failure in the probes silently declines to prompt.
   */
  async maybeNudgeAfterPasswordLogin(): Promise<void> {
    try {
      if (this.promptSubject.value) return;      // one prompt at a time
      if (this.isSnoozed()) return;
      if (!(await this.auth.passkeyUsable())) return;
      if (!(await this.hasNoPasskey())) return;
      this.promptSubject.next({
        kind: 'enrol-nudge',
        title: 'Set up a passkey for faster sign-in',
        body: "Skip the password next time — use your device's fingerprint, face, or PIN.",
        cta: 'Set up',
      });
    } catch {
      /* never block the app on a nudge probe */
    }
  }

  /**
   * Story 2 (SCRUM-1973). After a cross-device PASSKEY login (phone via QR /
   * security key), offer to add a passkey to THIS device — but only when this
   * device can make a platform passkey and the user hasn't snoozed. A login that
   * already used a platform (built-in) authenticator gets no offer.
   */
  async maybeOfferLocalPasskey(attachment: string | null | undefined): Promise<void> {
    try {
      if (this.promptSubject.value) return;
      if (this.isSnoozed()) return;
      if (attachment !== 'cross-platform') return;
      if (!(await platformAuthenticatorIsAvailable())) return;
      this.promptSubject.next({
        kind: 'local-offer',
        title: 'Add a passkey to this device',
        body: 'You signed in from another device. Add a passkey here to sign in faster next time.',
        cta: 'Add passkey',
      });
    } catch {
      /* best effort — never affects the successful login */
    }
  }

  /** True when the signed-in user has no passkey yet. Fails safe to false (a
   *  failed lookup must not trigger a nudge). */
  private async hasNoPasskey(): Promise<boolean> {
    try {
      const res = await firstValueFrom(this.auth.listPasskeys());
      return (res?.passkeys?.length ?? 0) === 0;
    } catch {
      return false;
    }
  }

  /**
   * User accepted — run the SAME enrolment ceremony account-settings uses, then
   * dismiss WITHOUT snoozing (they engaged). Throws on ceremony failure so the
   * caller can decide whether to keep the banner; a dismissed device prompt is
   * not treated as success.
   */
  async accept(): Promise<{ verified: boolean }> {
    if (this.busy) return { verified: false };
    this.busy = true;
    try {
      const res = await this.auth.registerPasskey();
      this.promptSubject.next(null);
      return res;
    } finally {
      this.busy = false;
    }
  }

  /** User chose "Not now" — snooze for the cooldown and hide. */
  dismiss(): void {
    this.snooze();
    this.promptSubject.next(null);
  }

  /** Hide the banner without snoozing (e.g. after a ceremony error so the user
   *  can be re-offered later). */
  clear(): void {
    this.promptSubject.next(null);
  }

  private snooze(): void {
    try {
      localStorage.setItem(this.SNOOZE_KEY, String(Date.now() + this.COOLDOWN_MS));
    } catch {
      /* storage unavailable — best effort */
    }
  }
}
